import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, Circle, Sparkle, FileDoc, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import Form1Form, { emptyForm1 } from "../components/forms/Form1Form";
import Form2Form, { emptyForm2 } from "../components/forms/Form2Form";
import Form3Medications, { emptyForm3 } from "../components/forms/Form3Medications";
import Form4CarePlan, { emptyForm4 } from "../components/forms/Form4CarePlan";
import CarePlanDictation from "../components/CarePlanDictation";
import CarePlanAccessPanel from "../components/CarePlanAccessPanel";
import ServicePlanSummary from "../components/ServicePlanSummary";
import { WrapMonitoringEditor } from "../components/WrapMonitoringEditor";
import { PlanVsActualCard } from "../components/PlanVsActualCard";
import { SatisfactionCard } from "../components/SatisfactionCard";
import Form5Approval, { emptyForm5 } from "../components/forms/Form5Approval";
import GettingToKnowYouForm, { emptyGty } from "../components/forms/GettingToKnowYouForm";
import { GuidedIntakeModal } from "../components/GuidedIntakeModal";
import { useAuth } from "../context/AuthContext";
import { ClientChecklist } from "../components/ClientChecklist";
import { VisitCalendar } from "../components/VisitCalendar";
import { ScheduleCard } from "../components/ScheduleCard";
import { CaregiverAckCard } from "../components/CaregiverAckCard";
import { AgreementCard } from "../components/AgreementCard";
import { IntakeQuestionnaireCard } from "../components/IntakeQuestionnaireCard";
import { HomeSafetyCard } from "../components/HomeSafetyCard";
import { HomeSafetyObservation } from "../components/HomeSafetyObservation";
import { ClientAppointmentsCard } from "../components/ClientAppointmentsCard";
import ClientWrapSettings from "../components/ClientWrapSettings";
import { PortalDialog } from "../components/PortalDialog";
import PersonCallLogCard from "../components/PersonCallLogCard";
import PersonNotesTrail from "../components/PersonNotesTrail";
import { ShareDialog } from "../components/ShareDialog";
import { IntakeLinkDialog } from "../components/IntakeLinkDialog";
import { ImportDialog } from "../components/ImportDialog";
import { ModulesDialog } from "../components/ModulesDialog";
import { HospitalizationDialog } from "../components/HospitalizationDialog";
import { IncidentReportDialog } from "../components/IncidentReportDialog";
import { GuidelinesNotice } from "../components/GuidelinesNotice";
import { PlanSignoffNotice } from "../components/PlanSignoffNotice";
import PairingsCard from "../components/PairingsCard";
import ClientAttentionCard from "../components/ClientAttentionCard";
import QuickNoteCard from "../components/QuickNoteCard";
import { DEMO } from "../demo/demoData";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const STEPS = [
  { key: "intake", label: "Client Identifying Information", desc: "Form 1 — master record, PHI", empty: emptyForm1, component: Form1Form },
  { key: "profile", label: "Intake Assessment", desc: "Form 2 — living, health, providers", empty: emptyForm2, component: Form2Form },
  { key: "gty", label: "Getting to Know You", desc: "Guided person-centered questions", empty: emptyGty, component: GettingToKnowYouForm },
  { key: "medications", label: "Medication List", desc: "Form 3 — repeatable, updatable", empty: emptyForm3, component: Form3Medications },
  { key: "careplan", label: "Care Plan Details", desc: "Form 4 — office completes: tasks, schedule, safety", empty: emptyForm4, component: Form4CarePlan },
  { key: "approval", label: "Care Plan Approval & Signatures", desc: "Form 5 — signatures & status", empty: emptyForm5, component: Form5Approval },
];

const PREFILL_STEPS = { profile: "home_visit", medications: "medications", gty: "gty" };

const GUIDED_FLOW = [
  { step: "intake", section: "form1", label: "Client Identifying Information" },
  { step: "profile", section: "assessment", label: "Intake Assessment" },
  { step: "medications", section: "medications", label: "Medication List" },
];

const merge = (empty, saved) => {
  if (!saved) return empty;
  const m = { ...empty, ...saved };
  if (empty.tasks && saved.tasks) {
    m.tasks = empty.tasks.map((t) => saved.tasks.find((s) => s.name === t.name) || t);
  }
  if (empty.areas && saved.areas) {
    m.areas = { ...empty.areas, ...saved.areas };
  }
  return m;
};

function ClientCaregivers({ clientId, isAdmin }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [assign, setAssign] = useState({});
  const API = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(() => {
    const url = isAdmin ? `/clients/${clientId}/visit-audit` : `/clients/${clientId}/caregivers`;
    api.get(url)
      .then((r) => {
        const d = r.data;
        setData(d.caregivers ? { caregivers: d.caregivers, unmatched: d.unmatched || [] }
                             : { caregivers: [], unmatched: [] });
      })
      .catch(() => setData({ caregivers: [], unmatched: [] }));
  }, [clientId, isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.users || [];
      setStaff(list.filter((u) => u.role === "staff"));
    }).catch(() => {});
  }, [isAdmin]);

  const linkName = (name) => {
    const uid = assign[name];
    if (!uid) return;
    api.post(`/users/${uid}/aliases`, { alias: name }).then(load).catch(() => {});
  };

  const caregivers = data?.caregivers || [];
  const unmatched = data?.unmatched || [];
  const auditLine = (label, st) => st && (
    <div className="text-[11px] leading-snug">
      <span className="text-stone-400">{label}: </span>
      {st.ok ? (
        <span className="font-medium text-emerald-700">file complete</span>
      ) : (
        <span className="text-[#8a5d14]">missing — {(st.missing || []).join("; ")}</span>
      )}
    </div>
  );

  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="client-caregivers-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-heading text-sm font-semibold text-stone-900">Caregivers who have worked with this client</p>
        <div className="flex items-center gap-3">
          <p className="text-xs text-stone-400">{data ? `${caregivers.length} people` : "Loading…"}</p>
          {isAdmin && (
            <a href={`${API}/api/clients/${clientId}/visit-audit.pdf`} download
              className="text-xs font-medium text-primary hover:underline"
              data-testid="visit-audit-pdf-button">Print file audit (PDF)</a>
          )}
        </div>
      </div>
      {caregivers.length > 0 && (
        <div className="mt-3 divide-y divide-stone-100" data-testid="client-caregivers-list">
          {caregivers.map((cg, i) => (
            <div key={cg.user_id || `n${i}`} className="py-2" data-testid={`client-caregiver-${cg.user_id || i}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs">
                {cg.user_id ? (
                  <button onClick={() => navigate(`/team/${cg.user_id}`)}
                    className="min-w-40 text-left font-medium text-primary hover:underline"
                    data-testid={`client-caregiver-link-${cg.user_id}`}>{cg.name}</button>
                ) : (
                  <span className="min-w-40 font-medium text-stone-800">{cg.name}</span>
                )}
                <span className="text-stone-500">{cg.visits} visit{cg.visits === 1 ? "" : "s"}</span>
                <span className="text-stone-400">first {cg.first_date}</span>
                <span className="text-stone-400">last {cg.last_date}</span>
              </div>
              {cg.as_of_first && (
                <div className="mt-1 space-y-0.5 pl-1">
                  {auditLine(`As of first visit (${cg.first_date})`, cg.as_of_first)}
                  {auditLine("Today", cg.today)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {unmatched.length > 0 && (
        <div className="mt-3 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-3" data-testid="client-caregivers-unmatched">
          <p className="text-xs font-semibold text-[#8a5d14]">Couldn't match these visit-log names to an employee file — place them:</p>
          {unmatched.map((u, i) => (
            <div key={`u${i}`} className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium text-stone-800">{u.name}</span>
              <span className="text-stone-400">{u.visits} visits, {u.first_date} → {u.last_date}</span>
              <select className="rounded border border-stone-300 bg-white px-1.5 py-0.5 text-xs"
                value={assign[u.name] || ""} data-testid={`assign-select-${i}`}
                onChange={(e) => setAssign({ ...assign, [u.name]: e.target.value })}>
                <option value="">this is…</option>
                {staff.map((s) => <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>)}
              </select>
              <button onClick={() => linkName(u.name)} disabled={!assign[u.name]}
                className="rounded bg-primary px-2 py-0.5 text-xs font-medium text-white disabled:opacity-40"
                data-testid={`assign-button-${i}`}>Link</button>
            </div>
          ))}
        </div>
      )}
      {data && caregivers.length === 0 && unmatched.length === 0 && (
        <p className="mt-3 text-xs italic text-stone-400" data-testid="client-caregivers-empty">No visits on record yet.</p>
      )}
    </div>
  );
}

function RecentVisits({ clientId }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      setBusy(true);
      try {
        const { data: res } = await api.get(`/clients/${clientId}/visits?days=${days}`);
        if (live) setData(res);
      } catch { if (live) setData({ visits: [], count: 0 }); }
      finally { if (live) setBusy(false); }
    })();
    return () => { live = false; };
  }, [clientId, days]);

  const visits = data?.visits || [];
  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="recent-visits-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-heading text-sm font-semibold text-stone-900">Recent visits</p>
        <p className="text-xs text-stone-400">{busy ? "Loading…" : `last ${data?.days ?? days} days · ${data?.count ?? 0} visits`}</p>
      </div>
      {visits.length > 0 ? (
        <div className="mt-3 space-y-1.5" data-testid="recent-visits-list">
          {visits.map((v) => (
            <div key={v.id} className="flex flex-wrap items-center gap-x-4 gap-y-0.5 rounded-lg border border-stone-100 px-4 py-2 text-xs" data-testid={`visit-row-${v.id}`}>
              <span className="font-medium text-stone-800">{v.visit_date}</span>
              <span className="text-stone-600">{v.caregiver_name}</span>
              <span className="text-stone-400">{v.worked_raw && v.worked_raw !== "-" ? v.worked_raw : v.scheduled_raw}</span>
              <span className="text-stone-500">{v.worked_minutes ? `${(v.worked_minutes / 60).toFixed(1)}h` : "—"}</span>
              {v.missed && <span className="rounded bg-[#C0392B]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#C0392B]">MISSED</span>}
              {v.short_visit && !v.missed && <span className="rounded bg-[#B7791F]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#8a5d14]">SHORT</span>}
            </div>
          ))}
        </div>
      ) : (
        !busy && <p className="mt-3 text-xs text-stone-400" data-testid="recent-visits-empty">No visits in the last {data?.days ?? days} days.</p>
      )}
      <div className="mt-3 flex gap-2">
        {days < 30 && <Button size="sm" variant="outline" onClick={() => setDays(30)} className="rounded-full border-stone-300 text-xs" data-testid="recent-visits-more">Show last 30 days</Button>}
        {days >= 30 && days < 3650 && <Button size="sm" variant="outline" onClick={() => setDays(3650)} className="rounded-full border-stone-300 text-xs" data-testid="recent-visits-all">Show all imported visits</Button>}
        {days > 7 && <Button size="sm" variant="ghost" onClick={() => setDays(7)} className="text-xs text-stone-400" data-testid="recent-visits-less">Back to last 7 days</Button>}
      </div>
    </div>
  );
}

const ServiceStatusBadge = ({ client, isAdmin, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const status = client.service_status || "active";
  const manual = client.service_status_set === "manual";
  const meta = {
    active: { label: "Active", cls: "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]" },
    paused: { label: "Paused", cls: "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]" },
    inactive: { label: "Inactive", cls: "border-stone-400 bg-stone-100 text-stone-500" },
  }[status];
  const setStatus = async (next) => {
    let reason = "";
    if (next === "paused" || next === "inactive") {
      reason = window.prompt(`Reason for marking ${next}? (required)`) || "";
      if (!reason.trim()) { toast.error("A reason is required"); return; }
    }
    setBusy(true);
    try {
      await api.put(`/clients/${client.id}/service-status`, { status: next, reason });
      toast.success(next === "auto" ? "Back on automatic tracking" : `Client marked ${next}`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <span className="mt-1.5 inline-flex flex-wrap items-center gap-2" data-testid="service-status">
      <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${meta.cls}`} data-testid="service-status-badge">
        {meta.label}{manual ? " (manual)" : ""}
      </span>
      {client.service_status_reason && <span className="text-[11px] italic text-stone-400" data-testid="service-status-reason">{client.service_status_reason}</span>}
      {client.last_visit_date && <span className="text-[11px] text-stone-400">last visit {client.last_visit_date}</span>}
      {isAdmin && (
        <select value="" disabled={busy} onChange={(e) => { if (e.target.value) setStatus(e.target.value); e.target.value = ""; }}
          className="h-6 rounded-md border border-stone-300 px-1 text-[11px] text-stone-500" data-testid="service-status-select">
          <option value="">Set status…</option>
          <option value="active">Active</option>
          <option value="paused">Paused (reason required)</option>
          <option value="inactive">Inactive (reason required)</option>
          {manual && <option value="auto">Release to automatic tracking</option>}
        </select>
      )}
    </span>
  );
};

function DcAddendumCard({ client, clientId, onSaved }) {
  const info = client.forms?.designated_caregiver_addendum;
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [cg, setCg] = useState(info?.caregiver_name || "");
  const [busy, setBusy] = useState(false);
  const stale = !!info?.signed_at && (Date.now() - new Date(info.signed_at).getTime()) / 86400000 > 365;
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/designated-caregiver-addendum`, { signed_date: date, caregiver_name: cg });
      toast.success("Addendum recorded — annual clock restarted");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="dc-addendum-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-heading text-sm font-semibold text-stone-900">Designated caregiver addendum</p>
          <p className="mt-0.5 text-xs text-stone-500" data-testid="dc-addendum-status">
            {info?.signed_at
              ? <>Signed {info.signed_at}{info.caregiver_name ? ` · caregiver: ${info.caregiver_name}` : ""}{stale ? " — annual re-signature due" : ""}</>
              : "Not on file — print it with the intake packet when this client designates their own caregiver. The client signs; the caregiver attests on their own form."}
          </p>
        </div>
        <a href={`${api.defaults.baseURL}/admin/forms/designated-caregiver-addendum.pdf?client=${encodeURIComponent(client.name || "")}&caregiver=${encodeURIComponent(cg)}`}
          target="_blank" rel="noreferrer" data-testid="dc-addendum-print"
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-300 bg-white px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white">
          Print addendum
        </a>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Date signed</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="dc-addendum-date" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Caregiver</label>
          <input value={cg} onChange={(e) => setCg(e.target.value)} placeholder="Caregiver name" className="h-9 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="dc-addendum-caregiver" />
        </div>
        <Button onClick={save} disabled={busy || !date} className="rounded-full bg-primary text-primary-foreground" data-testid="dc-addendum-save">
          {busy ? "Saving…" : info?.signed_at ? "Record re-signature" : "Record signature"}
        </Button>
      </div>
    </div>
  );
}

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [client, setClient] = useState(null);
  const [step, setStep] = useState("profile");
  const [formData, setFormData] = useState(null);
  const [loadedStep, setLoadedStep] = useState(null);
  const [saving, setSaving] = useState(false);
  const [guidedIdx, setGuidedIdx] = useState(null); // null = closed; 0..2 = which intake form the guided flow is on
  const [generating, setGenerating] = useState(false);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [prefillCount, setPrefillCount] = useState(0);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [pocBusy, setPocBusy] = useState(false);
  const pocRef = useRef(null);
  const pdfRef = useRef(null);
  const [parsing, setParsing] = useState(false);

  const [billRate, setBillRate] = useState("");
  const [showFin, setShowFin] = useState(false);
  useEffect(() => { api.get("/settings/features").then((r) => setShowFin(!!r.data.financials)).catch(() => {}); }, []);

  useEffect(() => { setBillRate(client?.bill_rate != null ? String(client.bill_rate) : ""); }, [client?.bill_rate]);

  const saveBillRate = async () => {
    try {
      await api.put(`/clients/${id}/bill-rate`, { bill_rate: billRate.trim() === "" ? null : parseFloat(billRate) || 0 });
      toast.success("Bill rate saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const BATCH_TARGETS = [["intake", "Intake info"], ["profile", "Assessment"], ["medications", "Medications"], ["careplan", "Care plan"], ["other", "Not a record form"]];
  const [batchOpen, setBatchOpen] = useState(false);
  const [parseEnabled, setParseEnabled] = useState(false);

  const openBatch = async () => {
    setBatchOpen(true);
    try { const { data } = await api.get("/settings/doc-parse"); setParseEnabled(!!data.enabled); } catch { /* default off */ }
  };

  const toggleParse = async (val) => {
    setParseEnabled(val);
    try {
      await api.put("/settings/doc-parse", { enabled: val });
      toast.success(val ? "AI document extraction enabled" : "AI document extraction disabled");
    } catch (e) { toast.error(formatApiError(e)); setParseEnabled(!val); }
  };
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchResults, setBatchResults] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);

  const parseBatch = async () => {
    if (!batchFiles.length) return;
    setBatchBusy(true);
    const fd = new FormData();
    batchFiles.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/clients/${id}/documents/parse-batch`, fd, { timeout: 300000 });
      setBatchResults(data.results);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBatchBusy(false);
    }
  };

  const applyBatch = async () => {
    setBatchBusy(true);
    const fd = new FormData();
    batchFiles.forEach((f) => fd.append("files", f));
    fd.append("payload", JSON.stringify((batchResults || []).filter((r) => !r.error && r.target !== "other" && Object.keys(r.fields || {}).length)));
    try {
      const { data } = await api.post(`/clients/${id}/documents/apply-batch`, fd, { timeout: 120000 });
      const n = Object.values(data.applied || {}).reduce((a, b) => a + b, 0);
      toast.success(`Applied ${n} fields to the record · ${data.attached} document(s) attached`);
      setBatchOpen(false);
      setBatchFiles([]);
      setBatchResults(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBatchBusy(false);
    }
  };

  const setBatchTarget = (i, target) => setBatchResults((cur) => cur.map((r, j) => (j === i ? { ...r, target } : r)));

  const applyDictationDraft = (draft) => {
    if (!draft || typeof draft !== "object") return;
    const fields = {};
    for (const k of ["need", "goal", "schedule_instructions", "routines_preferences", "safety_instructions",
                     "off_limits", "equipment_used", "transfer_instructions", "med_box_location",
                     "reminder_instructions", "transport_instructions", "backup_instructions", "verbal_summary"]) {
      if (typeof draft[k] === "string" && draft[k].trim()) fields[k] = draft[k].trim();
    }
    if (Array.isArray(draft.tasks) && draft.tasks.length) {
      const byCode = {};
      draft.tasks.forEach((t) => { if (t && t.code) byCode[String(t.code)] = t; });
      fields.tasks = (formData.tasks || []).map((t) => {
        const d = byCode[String(t.code)];
        return d ? { ...t, selected: true, levels: d.levels || [], frequency: d.frequency || "", instruction: d.instruction || "" } : t;
      });
    }
    setFormData((cur) => ({ ...cur, ...fields }));
  };

  const parsePdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setParsing(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/clients/${id}/forms/careplan/parse-upload`, fd, { timeout: 180000 });
      const n = Object.keys(data.fields || {}).length;
      const tk = (data.tasks || []).length;
      const hasSched = data.schedule && Object.keys(data.schedule).length > 0;
      if (n === 0 && tk === 0 && !hasSched) {
        toast.error("Couldn't find any care plan fields in that PDF");
      } else {
        setFormData((cur) => {
          const next = { ...cur, ...data.fields };
          if (tk) {
            const byCode = {};
            data.tasks.forEach((t) => { if (t && t.code) byCode[String(t.code)] = t; });
            next.tasks = (next.tasks || []).map((t) => {
              const d = byCode[String(t.code)];
              return d ? { ...t, selected: true, levels: t.levels || [], frequency: d.frequency || "", instruction: d.instruction || "" } : t;
            });
          }
          if (hasSched) next.schedule = { ...(next.schedule || {}), ...data.schedule };
          return next;
        });
        toast.success(`Filled ${n} fields${tk ? ` + ${tk} duties` : ""}${hasSched ? " + schedule" : ""} from the PDF — review them, then Save & Continue`);
        const intake = data.intake || {};
        if (Object.keys(intake).length && window.confirm("This PDF also has client header info (name, DOB, address, phone, payer). Fill the Intake step with it?")) {
          try {
            const cur2 = await api.get(`/clients/${id}`);
            const existing = cur2.data.forms?.intake || {};
            await api.put(`/clients/${id}/forms/intake`, { ...existing, ...intake });
            toast.success("Intake step filled from the PDF");
            load();
          } catch (err) { toast.error(formatApiError(err)); }
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setParsing(false);
    }
  };

  const load = () =>
    api.get(`/clients/${id}`)
      .then((r) => setClient(r.data))
      .catch((e) => { toast.error(formatApiError(e)); navigate("/"); });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (!client) return;
    setLoadedStep(null);
    const def = STEPS.find((s) => s.key === step);
    let merged = merge(def.empty, client.forms?.[step]);
    if (step === "careplan" && !merged.effective_date && client.service_start_date) {
      merged = { ...merged, effective_date: client.service_start_date };
    }
    const respKey = PREFILL_STEPS[step];
    const resp = respKey ? client.intake_responses?.[respKey] : null;
    let count = 0;
    if (resp) {
      if (step === "medications") {
        const filled = (resp.entries || []).filter((r) => r.name);
        const existing = (merged.entries || []).filter((r) => r.name);
        if (filled.length && existing.length === 0) {
          merged = { ...merged, entries: [...filled, ...Array.from({ length: Math.max(0, 5 - filled.length) }, () => ({ name: "", dosage: "", frequency: "" }))],
                     notes: merged.notes || resp.notes || "", source: merged.source || resp.source || "" };
          count = filled.length;
        }
      } else if (step === "gty") {
        const incoming = Object.fromEntries(Object.entries(resp.guided_answers || {}).filter(([k, v]) => v?.answer && !(merged.guided_answers || {})[k]?.answer));
        if (Object.keys(incoming).length || (!merged.anything_else && resp.anything_else)) {
          merged = { ...merged, guided_answers: { ...incoming, ...(merged.guided_answers || {}) }, anything_else: merged.anything_else || resp.anything_else || "" };
          count = Object.keys(incoming).length;
        }
      } else {
        const patch = {};
        for (const [k, v] of Object.entries(resp)) {
          if (k === "submitted_at" || k === "submitted_via") continue;
          if ((merged[k] === "" || merged[k] == null) && v !== "" && v != null) { patch[k] = v; count++; }
        }
        if (count) merged = { ...merged, ...patch };
      }
    }
    setPrefillCount(count);
    setFormData(merged);
    setLoadedStep(step);
  }, [client, step]);

  if (!client || !formData || loadedStep !== step) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const REQUIRED_FIELDS_JS = { intake: ["full_name", "dob", "address", "phone"], profile: ["assessment_date", "completed_by", "primary_diagnoses", "mobility_status"], gty: [], medications: ["date_reviewed", "source"], careplan: ["need", "goal"], approval: ["signer_type", "client_signature_date"] };
  const formComplete = (key, data) => !!data && (REQUIRED_FIELDS_JS[key] || []).every((f) => String(data[f] || "").trim());
  const completedCount = STEPS.filter((s) => formComplete(s.key, client.forms?.[s.key])).length;
  const allComplete = !!(client.forms?.profile && client.forms?.careplan);
  const ActiveForm = STEPS.find((s) => s.key === step).component;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/clients/${id}/forms/${step}`, formData);
      setClient(data);
      if (data.version_reset) {
        toast.warning("Care plan changed after approval — version bumped and Form 5 reset to Draft for re-signature.");
      }
      toast.success("Form saved");
      const nextIdx = STEPS.findIndex((s) => s.key === step) + 1;
      if (nextIdx < STEPS.length) setStep(STEPS[nextIdx].key);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/clients/${id}/generate`);
      const started = Date.now();
      while (Date.now() - started < 300000) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data } = await api.get(`/clients/${id}`);
        if (data.generation_status === "done") {
          toast.success("Care plan generated — 10 documents ready");
          navigate(`/clients/${id}/documents`);
          return;
        }
        if (data.generation_status === "error") {
          toast.error(data.generation_error || "Generation failed. Please retry.");
          return;
        }
      }
      toast.error("Generation is taking unusually long. Check back shortly.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const clientName = client.forms?.intake?.full_name || client.client_code;

  const remove = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted");
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadDemoStep = () => {
    setFormData(JSON.parse(JSON.stringify(DEMO[step])));
    toast.info("Demo data loaded — click Save & Continue to store it");
  };

  const loadAllDemo = async () => {
    setLoadingDemo(true);
    try {
      for (const s of STEPS) {
        await api.put(`/clients/${id}/forms/${s.key}`, DEMO[s.key]);
      }
      await load();
      toast.success("Demo data saved for all 5 forms");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingDemo(false);
    }
  };

  return (
    <div data-testid="client-detail-page">
      <GuidelinesNotice client={client} />
      <PlanSignoffNotice client={client} />
      {(client.care_updates || []).length > 0 && (() => {
        const latest = [...(client.care_updates || [])].sort((a, b) => (b.at || "").localeCompare(a.at || ""))[0];
        const isHosp = latest.kind === "hospitalization";
        return (
          <div className={`mb-4 rounded-lg border p-4 ${isHosp ? "border-[#C0392B]/30 bg-[#C0392B]/5" : "border-[#B7791F]/30 bg-[#B7791F]/5"}`} data-testid="care-update-banner">
            <p className={`text-xs font-semibold ${isHosp ? "text-[#C0392B]" : "text-[#8a5d14]"}`}>
              {isHosp ? "Hospitalization / facility update" : "Schedule change"} · {latest.entry_date || (latest.at || "").slice(0, 10)} · via {latest.via || "office"}
            </p>
            <p className="mt-1 text-sm text-stone-700">{latest.summary}</p>
          </div>
        );
      })()}
      {isAdmin && <ClientAttentionCard clientId={id} />}
      <ClientWrapSettings client={client} isAdmin={isAdmin} onChanged={load} />
      {isAdmin && <div className="mb-4"><PairingsCard cid={id} /></div>}
      <div className="mb-4"><QuickNoteCard presetClient={{ id, name: client.name }} compact /></div>
      <HomeSafetyObservation clientId={id} />
      <button onClick={() => navigate("/")} className="mb-4 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="back-to-dashboard">
        <ArrowLeft size={14} /> Back to clients
      </button>

      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900" data-testid="client-name">
              {clientName}
            </h1>
          </div>
          <p className="mt-1 text-sm text-stone-500">{completedCount}/{STEPS.length} forms complete{client.generated_at ? ` · Plan generated ${client.generated_at.slice(0, 10)}` : ""}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="client-contact-buttons">
            {(client.forms?.intake?.phone || "").trim() && (
              <a href={`tel:${(client.forms.intake.phone || "").replace(/[^+\d]/g, "")}`}
                className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-transform hover:scale-95"
                data-testid="client-call-link">
                Call {client.forms.intake.phone}
              </a>
            )}
            {(client.forms?.intake?.email || "").trim() && (
              <a href={`mailto:${client.forms.intake.email}`}
                className="inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white"
                data-testid="client-email-link">
                Email {client.forms.intake.email}
              </a>
            )}
          </div>
          <ServiceStatusBadge client={client} isAdmin={isAdmin} onChanged={load} />
          {isAdmin && (
            <p className="mt-1 flex items-center gap-2 text-xs text-stone-500" data-testid="billing-rate-editor">
              Billing rate $
              <input type="number" step="0.01" key={client.billing_rate ?? 21.57} defaultValue={client.billing_rate ?? 21.57}
                onBlur={async (e) => {
                  const v = parseFloat(e.target.value);
                  if (v && v !== client.billing_rate) {
                    try {
                      await api.put(`/clients/${id}/billing-rate`, { rate: v });
                      toast.success(`Billing rate $${v}/hr saved`);
                      load();
                    } catch (err) { toast.error(formatApiError(err)); }
                  }
                }}
                className="h-7 w-20 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="billing-rate-input" />
              /hr (CHC default 21.57)
            </p>
          )}
          {isAdmin && <WrapMonitoringEditor clientId={id} />}
          {isAdmin && <PlanVsActualCard clientId={id} clientName={client?.forms?.intake?.full_name || client?.client_code || ""} />}
          {isAdmin && <SatisfactionCard clientId={id} />}
          {isAdmin && (
            <p className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-stone-500" data-testid="overnight-risk-editor">
              Overnight check-in
              <select value={client.overnight_risk || ""}
                onChange={async (e) => {
                  try {
                    await api.put(`/clients/${id}/overnight-risk`, { risk: e.target.value });
                    toast.success("Overnight classification saved");
                    load();
                  } catch (err) { toast.error(formatApiError(err)); }
                }}
                className={`h-7 rounded-md border px-2 text-xs ${client.overnight_risk === "safety" ? "border-[#C0392B]/40 bg-[#C0392B]/5 font-semibold text-[#C0392B]" : "border-stone-300 bg-white"}`}
                data-testid="overnight-risk-select">
                <option value="">Not on overnight duty</option>
                <option value="administrative">Overnight — administrative</option>
                <option value="safety">Overnight — SAFETY-critical</option>
              </select>
              {client.overnight_risk === "safety" && <span className="text-[10px] text-[#C0392B]">a missed check-in escalates to the office at 5:15am</span>}
            </p>
          )}
          {isAdmin && showFin && (
            <p className="mt-1.5 flex items-center gap-2 text-xs text-stone-500" data-testid="bill-rate-editor">
              Bill rate $/hr
              <input value={billRate} onChange={(e) => setBillRate(e.target.value)}
                className="h-7 w-20 rounded-md border border-stone-300 px-2 text-xs" data-testid="bill-rate-input" placeholder="CHC default" />
              <button onClick={saveBillRate} className="rounded-full bg-stone-900 px-2.5 py-1 text-xs font-semibold text-white hover:bg-stone-700" data-testid="bill-rate-save">Save</button>
              <span className="text-stone-400">blank = CHC default ($21.57)</span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => navigate(`/clients/intake/${id}`)} data-testid="client-intake-button"
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#4A235A] px-4 text-sm font-semibold text-white transition-colors hover:bg-stone-900">
            Intake & Questionnaire
          </button>
          {client.forms?.careplan && (
            <button onClick={() => navigate(`/clients/${id}/careplan-review`)} data-testid="careplan-review-button"
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#4A235A]/40 bg-white px-4 text-sm font-medium text-[#4A235A] transition-colors hover:bg-[#4A235A] hover:text-white">
              Care Plan — Review & Edit
            </button>
          )}
          <Button onClick={() => navigate(`/clients/${id}/paperwork`)} data-testid="paperwork-button"
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-stone-900 px-4 text-sm font-semibold text-white transition-colors hover:bg-stone-700">
            Paperwork & Signing
          </Button>
          {client.documents && (
            <Button variant="outline" onClick={() => navigate(`/clients/${id}/documents`)} className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="view-documents-button">
              <FileDoc size={16} className="mr-1.5" /> View Documents
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate(`/call-log?client=${id}`)} className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="log-call-button">
            Log a call / note
          </Button>
          <button onClick={() => setMoreOpen((o) => !o)} data-testid="more-actions-toggle"
            className="text-xs font-medium text-stone-400 underline transition-colors hover:text-stone-700">
            {moreOpen ? "Fewer actions ▲" : "More actions ▼"}
          </button>
        </div>
        {moreOpen && (
        <div className="mt-3 flex w-full flex-wrap gap-3 rounded-xl border border-stone-200 bg-stone-50 p-4" data-testid="more-actions-row">
          <IntakeLinkDialog clientId={id} client={client} />
          <Button onClick={() => setGuidedIdx(0)} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="guided-intake-top-button">
            Guided Intake — ask me one at a time
          </Button>
          <PortalDialog clientId={id} />
          <ImportDialog clientId={id} client={client} />
          <ModulesDialog clientId={id} client={client} />
          <ShareDialog clientId={id} clientCode={client.client_code} />
          <Button variant="outline" onClick={() => navigate(`/clients/${id}/log`)} className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="shift-log-button">
            Shift Log
          </Button>
          {user?.role === "admin" && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/clients/${id}/visit-pack.pdf`} download
              className="inline-flex h-10 items-center rounded-md bg-[#4A235A] px-4 text-sm font-medium text-white hover:bg-[#3a1c47]"
              data-testid="visit-pack-button">Visit pack (print for the visit)</a>
          )}
          <input type="file" accept="application/pdf" ref={pocRef} className="hidden" data-testid="poc-upload-input"
            onChange={async (e) => {
              const f = e.target.files?.[0]; e.target.value = "";
              if (!f) return;
              setPocBusy(true);
              try {
                const fd = new FormData(); fd.append("file", f);
                const { data } = await api.post(`/clients/${id}/poc-upload`, fd, { timeout: 120000 });
                toast.success(data.created ? `POC saved — ${data.tasks} tasks read (start ${data.poc_start_date || "?"})` : "That exact POC file is already on file");
              } catch (e2) { toast.error(formatApiError(e2)); } finally { setPocBusy(false); }
            }} />
          <Button variant="outline" onClick={() => pocRef.current?.click()} disabled={pocBusy} className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="poc-upload-button">
            {pocBusy ? "Reading POC…" : "Upload POC"}
          </Button>
          <Button variant="outline" onClick={() => navigate(`/clients/${id}/incidents`)} className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="incidents-button">
            Incidents
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={openBatch} className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="batch-extract-button">
              Extract from Documents
            </Button>
          )}
          {batchOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="batch-extract-modal">
              <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
                <p className="font-heading text-lg font-semibold text-stone-900">Extract record from documents</p>
                <p className="mt-1 text-xs text-stone-500">Drop a stack of the client's existing PDFs — the app identifies each one and maps it onto their record sections. Nothing is saved until you apply.</p>
                <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-3 text-xs text-[#8a5d14]" data-testid="doc-parse-toggle-label">
                  <input type="checkbox" className="mt-0.5" checked={parseEnabled} onChange={(e) => toggleParse(e.target.checked)} data-testid="doc-parse-toggle" />
                  <span><strong>AI extraction is {parseEnabled ? "ON" : "OFF"}.</strong> While client data is masked, keep this off so uploaded documents that contain client names are not sent for parsing. Turn it on only when you're ready to extract real documents.</span>
                </label>
                <input type="file" accept=".pdf" multiple onChange={(e) => { setBatchFiles(Array.from(e.target.files || [])); setBatchResults(null); }}
                  className="mt-3 text-xs text-stone-500 file:mr-2 file:rounded-md file:border-0 file:bg-stone-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold" data-testid="batch-file-input" />
                {batchFiles.length > 0 && !batchResults && (
                  <Button onClick={parseBatch} disabled={batchBusy} className="mt-3 bg-primary text-primary-foreground" data-testid="batch-parse-button">
                    {batchBusy ? "Reading documents…" : `Analyze ${batchFiles.length} document(s)`}
                  </Button>
                )}
                {batchResults && (
                  <div className="mt-4 space-y-2" data-testid="batch-results">
                    {batchResults.map((r, i) => (
                      <div key={i} className="rounded-lg border border-stone-200 p-3 text-xs" data-testid={`batch-result-${i}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium text-stone-800">{r.filename}</span>
                          <select value={r.target} onChange={(e) => setBatchTarget(i, e.target.value)}
                            className="h-7 rounded-md border border-stone-300 px-1.5 text-xs" data-testid={`batch-target-${i}`}>
                            {BATCH_TARGETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </div>
                        {r.error
                          ? <p className="mt-1 text-[#C0392B]">Couldn't read: {r.error}</p>
                          : <p className="mt-1 text-stone-500">{Object.keys(r.fields || {}).length} fields found{Object.keys(r.fields || {}).length > 0 ? ` — ${Object.keys(r.fields).slice(0, 5).join(", ")}${Object.keys(r.fields).length > 5 ? "…" : ""}` : ""}</p>}
                      </div>
                    ))}
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="ghost" onClick={() => setBatchResults(null)} className="text-xs" data-testid="batch-back">Back</Button>
                      <Button onClick={applyBatch} disabled={batchBusy} className="bg-primary text-primary-foreground" data-testid="batch-apply-button">
                        {batchBusy ? "Applying…" : "Apply to record & attach PDFs"}
                      </Button>
                    </div>
                  </div>
                )}
                <div className="mt-4 text-right">
                  <Button variant="ghost" onClick={() => { setBatchOpen(false); setBatchResults(null); setBatchFiles([]); }} className="text-xs text-stone-500" data-testid="batch-close">Close</Button>
                </div>
              </div>
            </div>
          )}
          <IncidentReportDialog clientId={id} onReported={load} />
          {client.forms?.careplan && (
            <a href={`${api.defaults.baseURL}/clients/${id}/packet.pdf`} download data-testid="caregiver-packet-button"
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-300 bg-white px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white">
              Caregiver Packet
            </a>
          )}
          <a href={`${api.defaults.baseURL}/clients/${id}/service-information.pdf`} target="_blank" rel="noreferrer" data-testid="service-info-button"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-300 bg-white px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white">
            Service Information
          </a>
          <a href={`${api.defaults.baseURL}/clients/${id}/welcome-letter.pdf`} target="_blank" rel="noreferrer" data-testid="welcome-letter-button"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-300 bg-white px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white">
            Welcome Letter
          </a>
          <a href={`${api.defaults.baseURL}/clients/${id}/export.zip`} download data-testid="export-record-button"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-300 bg-white px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white">
            Export Record
          </a>
          <a href={`${api.defaults.baseURL}/clients/${id}/packet`} target="_blank" rel="noreferrer" data-testid="print-packet-button"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-300 bg-white px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white">
            Print Packet
          </a>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={loadingDemo} className="border-stone-300 text-stone-700 hover:bg-accent" data-testid="load-all-demo-button">
                {loadingDemo ? "Loading demo..." : "Load All Demo Data"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="demo-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Load demo data into all 5 forms?</AlertDialogTitle>
                <AlertDialogDescription>This overwrites any existing assessment data for {client.client_code} with the sample client (Harold Jennings scenario). Generated documents are kept until you regenerate.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="demo-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={loadAllDemo} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="demo-confirm">Load Demo Data</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button onClick={generate} disabled={!allComplete || generating} data-testid="generate-care-plan-button"
            className="bg-primary text-primary-foreground hover:bg-stone-900 disabled:opacity-50">
            <Sparkle size={16} className="mr-1.5" weight="fill" />
            {generating ? "Generating (this takes ~1-2 min)..." : client.documents ? "Regenerate Care Plan" : "Generate Care Plan"}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="border-stone-300 text-stone-500 hover:bg-[#C0392B] hover:text-white" data-testid="delete-client-button"><Trash size={16} /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this client?</AlertDialogTitle>
                <AlertDialogDescription>This removes all assessment forms and generated documents for {client.client_code}. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="bg-[#C0392B] text-white hover:bg-[#a93226]" data-testid="delete-confirm">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        )}
      </div>

      <ClientChecklist clientId={id} />

      <PersonNotesTrail testid="client-notes-trail"
        onToggleShare={isAdmin ? async (n, shared) => {
          try {
            await api.post(`/clients/${id}/notes/${n.id}/share`, { shared });
            toast.success(shared ? "Note shared to the caregiver notebook" : "Note removed from the caregiver notebook");
            load();
          } catch (e) { toast.error(formatApiError(e)); }
        } : undefined}
        notes={(client.shift_notes || []).map((n) => ({ id: n.id, text: n.note, type: n.log_type, date: n.shift_date || n.created_at, by: n.author, tags: n.tags || [], shared: !!n.shared_with_staff }))} />

      <ClientCaregivers clientId={id} isAdmin={user?.role === "admin"} />
      <RecentVisits clientId={id} />
      <PersonCallLogCard kind="client" pid={id} />

      <div className="mb-6"><VisitCalendar clientId={id} /></div>
      <ScheduleCard clientId={id} isAdmin={user?.role === "admin"} />
      <CaregiverAckCard clientId={id} />
      {user?.role === "admin" && <DcAddendumCard client={client} clientId={id} onSaved={load} />}

      {(client.client_uploads || []).length > 0 && (
        <div className="mb-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="client-uploads-card">
          <p className="font-heading text-sm font-semibold text-stone-900">Client uploads</p>
          <div className="mt-3 space-y-2">
            {client.client_uploads.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3 rounded-lg border border-stone-100 px-4 py-2.5" data-testid={`client-upload-${u.id}`}>
                <p className="text-sm text-stone-700">
                  <span className="font-medium">{({ discharge_instructions: "Discharge instructions", med_list: "Medication list", poa: "Power of Attorney" })[u.kind] || "Other document"}</span>
                  {" — "}{u.filename} <span className="text-xs text-stone-400">{String(u.uploaded_at).slice(0, 10)} · {u.via === "intake-link" ? "via intake link" : "office"}</span>
                </p>
                <Button size="sm" variant="outline" className="rounded-full border-stone-300" data-testid={`client-upload-open-${u.id}`}
                  onClick={async () => {
                    try {
                      const res = await api.get(`/clients/${id}/uploads/${u.id}`, { responseType: "blob" });
                      window.open(URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] })), "_blank");
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}>Open</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {user?.role === "admin" && <div className="mb-6"><IntakeQuestionnaireCard clientId={id} /></div>}
      {user?.role === "admin" && <div className="mb-6"><AgreementCard clientId={id} onChanged={load} /></div>}
      <div className="mb-6"><HomeSafetyCard clientId={id} onChanged={load} /></div>
      <div className="mb-6"><ClientAppointmentsCard clientId={id} appointments={client.client_appointments} onChanged={load} /></div>

      {generating && (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm text-primary" data-testid="generating-banner">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          AI is analyzing all four assessments across 10 dimensions and writing your documents. Please keep this page open.
        </div>
      )}

      {!generating && client.generation_status === "error" && (
        <div className="mb-6 rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/5 p-4 text-sm text-[#C0392B]" data-testid="generation-error-banner">
          Care plan generation failed: {client.generation_error || "unknown error"}. Click Generate Care Plan to try again — if it keeps failing, tell Kelly what this message says.
        </div>
      )}

      <ServicePlanSummary client={client} />

      <button onClick={() => setLegacyOpen((o) => !o)} data-testid="legacy-forms-toggle"
        className="mb-4 flex w-full items-center justify-between rounded-xl border border-dashed border-stone-300 bg-stone-50 px-5 py-3 text-left text-xs font-semibold text-stone-500 transition-colors hover:bg-stone-100">
        <span>{legacyOpen ? "Hide" : "Show"} the old form-by-form editor (Forms 1–5) — kept for reference; the Intake page and Review &amp; Sign flow above are the way forward</span>
        <span>{legacyOpen ? "▲" : "▼"}</span>
      </button>
      {legacyOpen && (
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr]">
        <nav className="space-y-2 lg:sticky lg:top-24 lg:self-start" data-testid="form-stepper">
          {STEPS.map((s, i) => {
            const done = !!client.forms?.[s.key];
            const active = step === s.key;
            return (
              <button key={s.key} onClick={() => setStep(s.key)} data-testid={`step-${s.key}`}
                className={`flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  active ? "border-primary bg-white shadow-sm" : "border-stone-200 bg-white/60 hover:bg-white"
                }`}>
                {done
                  ? <CheckCircle size={22} weight="fill" className="shrink-0 text-[#27AE60]" />
                  : <Circle size={22} className={`shrink-0 ${active ? "text-primary" : "text-stone-300"}`} />}
                <span>
                  <span className={`block text-sm font-semibold ${active ? "text-primary" : "text-stone-800"}`}>{i + 1}. {s.label}</span>
                  <span className="block text-xs text-stone-500">{s.desc}</span>
                </span>
              </button>
            );
          })}
        </nav>

        <div>
          {prefillCount > 0 && (
            <div className="mb-4 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-4 text-sm text-[#8a5d14]" data-testid="client-reported-banner">
              {prefillCount} answer{prefillCount === 1 ? "" : "s"} pre-filled from the client's own intake submission (client-reported) — review and adjust before saving.
            </div>
          )}
          {step === "careplan" && client.intake_responses?.needs && (
            <div className="mb-4 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="client-needs-panel">
              <p className="font-heading text-sm font-semibold text-stone-900">What the client told us they need</p>
              {(client.intake_responses.needs.help_areas || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {client.intake_responses.needs.help_areas.map((a) => (
                    <span key={a} className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground">{a}</span>
                  ))}
                </div>
              )}
              <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-stone-600 sm:grid-cols-3">
                {client.intake_responses.needs.mobility && <span>Mobility: {client.intake_responses.needs.mobility}</span>}
                {client.intake_responses.needs.transfer_assist && <span>Physical help: {client.intake_responses.needs.transfer_assist}</span>}
                {client.intake_responses.needs.memory && <span>Memory: {client.intake_responses.needs.memory}</span>}
              </div>
              {client.intake_responses.needs.description && (
                <p className="mt-2 text-xs italic leading-relaxed text-stone-500">"{client.intake_responses.needs.description}"</p>
              )}
            </div>
          )}
          <ActiveForm data={formData} onChange={setFormData} clientId={id} clientCode={clientName} />
          {step === "careplan" && <CarePlanAccessPanel clientId={id} clientName={clientName} />}
          <div className="mt-8 flex justify-end gap-3">
            {step === "careplan" && (
              <>
                <input type="file" accept=".pdf" ref={pdfRef} className="hidden" data-testid="parse-pdf-input" onChange={parsePdf} />
                <Button variant="outline" onClick={() => pdfRef.current?.click()} disabled={parsing} className="border-stone-300 text-stone-700 hover:bg-accent" data-testid="parse-pdf-button">
                  {parsing ? "Reading PDF…" : "Upload PDF to prefill"}
                </Button>
                <CarePlanDictation clientId={id} onApply={applyDictationDraft} />
              </>
            )}
            {["intake", "profile", "medications"].includes(step) && (
              <Button variant="outline" onClick={() => setGuidedIdx(GUIDED_FLOW.findIndex((f) => f.step === step))} className="border-primary/40 text-primary hover:bg-accent" data-testid="guided-intake-button">
                Ask me one at a time
              </Button>
            )}
            <Button variant="outline" onClick={loadDemoStep} className="border-stone-300 text-stone-700 hover:bg-accent" data-testid="load-demo-button">
              Load Demo Data
            </Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="save-form-button">
              {saving ? "Saving..." : "Save & Continue"}
            </Button>
          </div>
          {guidedIdx !== null && (
            <GuidedIntakeModal
              clientId={id}
              clientName={client?.forms?.intake?.full_name || ""}
              startIdx={guidedIdx}
              initialForms={{ ...(client?.forms || {}), [step]: formData || {} }}
              onClose={() => { setGuidedIdx(null); load(); }}
            />
          )}
        </div>
      </div>
      )}
    </div>
  );
}
